import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { HttpParams } from "@angular/common/http";
import { forkJoin, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { WebService } from "../../services/web/web.service";
import { SpinnerService } from "../../services/spinner/spinner.service";
import { ToastService } from "../../services/notification/toast.service";
import { RbacService } from "../../onboarding-users-roles/rbac.service";
import { SecurityService } from "../../services/security.service";

interface RoleOption {
  id: number | string;
  name: string;
  description: string;
  editable: boolean;
}

@Component({
  selector: "app-onboarding-user-update",
  templateUrl: "./onboarding-user-update.component.html",
  styleUrls: ["./onboarding-user-update.component.scss"],
  standalone : false,
})
export class OnboardingUserUpdateComponent implements OnInit {
  userForm: FormGroup;
  userId: number;
  roles: RoleOption[] = [];
  currentRoleIds: Array<number | string> = [];
  statusOptions = [
    { value: 1, label: 'Activo' },
    { value: 2, label: 'Inactivo' }
  ];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private webService: WebService,
    private rbacService: RbacService,
    private spinner: SpinnerService,
    private toast: ToastService,
    private securityService: SecurityService
  ) {
    this.userId = 0;
    this.userForm = this.formBuilder.group({
      name_user: ["", [Validators.required, Validators.minLength(2)]],
      lastName_user: ["", [Validators.required, Validators.minLength(2)]],
      email_user: ["", [Validators.required, Validators.email]],
      status_user: [1, [Validators.required]],
      roleId: [""],
    });
  }

  ngOnInit(): void {
    console.log("📝 Inicializando componente de edición de usuario");
    this.activatedRoute.params.subscribe((params) => {
      this.userId = params["id"];
      this.loadRoles();
      this.loadUser();
    });
  }

  loadUser() {
    console.log(`🔍 Cargando datos del usuario ID: ${this.userId}`);
    this.spinner.open();

    const partnerId = this.securityService.getTokenPartner();
    if (!partnerId) {
      console.warn("⚠️ No se encontró partner ID en sesión");
      this.spinner.close();
      return this.toast.showInfo("No se puede cargar usuario porque no se encontró un partner.");
    }

    this.webService
      .get(`${this.webService.HOST}/api/users/onboarding-admin/${this.userId}?partnerId=${partnerId}`)
      .subscribe(
        (response) => {
          console.log("✅ Usuario cargado exitosamente");
          const user = response["result"];
          this.userForm.patchValue({
            name_user: user.name_user,
            lastName_user: user.lastName_user,
            email_user: user.email_user,
            status_user: user.status_user,
          });
          this.loadUserRoles(partnerId);
        },
        (error) => {
          console.error("❌ Error al cargar usuario:", error.message || error);
          this.spinner.close();
          this.toast.showError(error);
          this.goBack();
        }
      );
  }

  save() {
    if (this.userForm.invalid) {
      this.toast.showWarning("Por favor complete todos los campos requeridos correctamente");
      return;
    }

    console.log("💾 Actualizando usuario");
    this.spinner.open();

    const partnerId = this.securityService.getTokenPartner();
    if (!partnerId) {
      console.warn("⚠️ No se encontró partner ID en sesión");
      this.spinner.close();
      return this.toast.showInfo("No se puede actualizar usuario porque no se encontró un partner.");
    }

    const body = {
      partnerId: partnerId,
      name_user: this.userForm.value.name_user,
      lastName_user: this.userForm.value.lastName_user,
      email_user: this.userForm.value.email_user,
      status_user: this.userForm.value.status_user,
    };

    this.webService
      .put(body, `${this.webService.HOST}/api/users/onboarding-admin/${this.userId}`)
      .pipe(switchMap(() => this.syncUserRole(Number(partnerId))))
      .subscribe(
        () => {
          console.log("✅ Usuario actualizado exitosamente");
          this.spinner.close();
          this.toast.showSuccess("Usuario actualizado exitosamente");
          this.goBack();
        },
        (error) => {
          console.error("❌ Error al actualizar usuario:", error.message || error);
          this.spinner.close();
          this.toast.showError(error);
        }
      );
  }

  loadRoles() {
    const partnerId = this.securityService.getTokenPartner();
    if (!partnerId) {
      this.roles = [];
      return;
    }

    this.rbacService.listRoles(Number(partnerId)).subscribe(
      (response) => {
        this.roles = this.extractRoles(response)
          .map((role: any) => ({
            id: role.id || role.roleId || role.role_id,
            name: role.name || role.roleName || role.nombre,
            description: role.description || role.descripcion || "",
            editable: role.editable !== false,
          }))
          .filter((role: RoleOption) => role.id && role.name);
      },
      (error) => {
        this.roles = [];
        this.toast.showError(error);
      }
    );
  }

  loadUserRoles(partnerId: number | string) {
    const params = new HttpParams()
      .set("pageIndex", "-1")
      .set("pageSize", "-1");

    this.rbacService.listUsers(Number(partnerId), params).subscribe(
      (response) => {
        const user = this.extractUsers(response).find((item: any) => Number(item.id) === Number(this.userId));
        const userRoles = Array.isArray(user?.roles) ? user.roles : [];
        this.currentRoleIds = userRoles.map((role: any) => role.id).filter(Boolean);
        this.userForm.patchValue({ roleId: this.currentRoleIds[0] || "" });
        this.spinner.close();
      },
      (error) => {
        this.currentRoleIds = [];
        this.spinner.close();
        this.toast.showError(error);
      }
    );
  }

  syncUserRole(partnerId: number) {
    const selectedRoleId = this.userForm.value.roleId;
    const requests = this.currentRoleIds
      .filter((roleId) => String(roleId) !== String(selectedRoleId))
      .map((roleId) => this.rbacService.removeRoleFromUser(partnerId, this.userId, roleId));

    if (selectedRoleId && !this.currentRoleIds.some((roleId) => String(roleId) === String(selectedRoleId))) {
      requests.push(this.rbacService.addRoleToUser(partnerId, this.userId, selectedRoleId));
    }

    return requests.length ? forkJoin(requests) : of(null);
  }

  private extractRoles(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data?.roles)) return response.data.roles;
    if (Array.isArray(response?.result?.data?.roles)) return response.result.data.roles;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.roles)) return response.roles;
    return [];
  }

  private extractUsers(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.data?.users)) return response.data.users;
    if (Array.isArray(response?.users)) return response.users;
    return [];
  }

  goBack() {
    if (this.activatedRoute.snapshot.queryParamMap.get("returnTo") === "onboarding-users-roles") {
      this.router.navigate(["/onboarding-users-roles"], {
        queryParams: { tab: this.activatedRoute.snapshot.queryParamMap.get("tab") || "users" },
      });
      return;
    }

    this.router.navigate(["../../"], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: "preserve",
    });
  }
}
